import styles from './ListingScore.module.css';
import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { listingScoreStorage } from '../../utils/listingScoreStorage';
import AuthContext from '../../context/AuthContext';
import LoadingOverlay from '../UI/LoadingOverlay';

import QueueNotificationPopup from '../UI/QueueNotificationPopup';
import SearchSection from './components/SearchSection';
import ResultsSection from './components/ResultsSection';
import ActionPlanSection from './components/ActionPlanSection';
import ComponentAnalysisSection from './components/ComponentAnalysisSection';
import AsinNotFoundModal from './components/AsinNotFoundModal';
import SelectProductCatalogueModal from '../UI/SelectProductCatalogueModal';
import { navigateToSingleListingUpgrade } from '../../utils/upgradeListingNavigation';
import { useAmazonSync } from '../../context/AmazonSyncContext';



const ListingScore = () => {
    // State management for core functionality
    const authCtx = useContext(AuthContext);
    const navigate = useNavigate();
    const { isBridgeActive } = useAmazonSync();
    const [asin, setAsin] = useState('');
    const [loading, setLoading] = useState(false);
    const [productData, setProductData] = useState(null);
    const [error, setError] = useState('');
    const [showAsinNotFoundModal, setShowAsinNotFoundModal] = useState(false);
    
    // Product Catalogue Modal State
    const [showProductModal, setShowProductModal] = useState(false);
    const [products, setProducts] = useState([]);
    const [selectedAsins, setSelectedAsins] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [loadingProducts, setLoadingProducts] = useState(false);
    
    // Queue notification popup state
    const [showQueuePopup, setShowQueuePopup] = useState(false);

    // Source tracking: "catalog" (from own catalogue, free) or "rainforest" (open search, credits)
    const [source, setSource] = useState('rainforest');
    
    // UI State management for interactive elements
    const [showDetailedAnalysis, setShowDetailedAnalysis] = useState(true);

    // Check for stored data from landing page on component mount
    useEffect(() => {
        // Check if this is from landing page unlock flow
        const isFromLandingPage = listingScoreStorage.getLandingPageFlag();
        
        if (isFromLandingPage) {
            // Get stored data
            const stored = JSON.parse(localStorage.getItem('landing_page_listing_score') || 'null');
            if (stored && stored.fullData) {
                // stored.fullData is the full API response; actual product data is in .data
                const full = stored.fullData;
                const parsedProduct = full && full.data ? full.data : full;
                setProductData(parsedProduct);
                setAsin(stored.asin || (parsedProduct && parsedProduct.asin) || '');
                
                
                // Clear the stored data after using it
                listingScoreStorage.clear();
            }
        }
    }, [authCtx]);

    // Make API call when page opens if Amazon is connected
    useEffect(() => {
        const isAmazonConnected = authCtx.user?.amazon_status === true;
        if (isAmazonConnected && authCtx.token) {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            fetch(`${API_URL}/oauth_amazon/amazon/catalogue`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json',
                },
            }).catch(err => {
                // Silently handle errors
            });
        }
    }, [authCtx.user?.amazon_status, authCtx.token]);



    // Resolve which source to use for a fresh submission.
    //   - Explicit catalogue modal pick (source='catalog') is always honoured.
    //   - Amazon NOT connected → Rainforest (no catalogue to hit).
    //   - Sync STILL RUNNING → Rainforest (catalogue empty during bridge).
    //   - Open accounts post-sync → Rainforest (can analyse external ASINs).
    //   - Closed accounts post-sync → catalogue only. External ASIN miss shows
    //     "not found" instead of silently burning Rainforest credits.
    const resolveSource = () => {
        // TEMP: Amazon catalogue under maintenance — always use Rainforest.
        return 'rainforest';
        // Original logic preserved below for easy revert.
        // if (source === 'catalog') return 'catalog';
        // const amazonConnected = authCtx?.user?.amazon_status === true;
        // if (!amazonConnected) return 'rainforest';
        // if (isBridgeActive) return 'rainforest';
        // const accountType = authCtx?.user?.account_type || 'close';
        // if (accountType === 'open') return 'rainforest';
        // return 'catalog';
    };

    // Main API call handler for product analysis
    const handleSubmit = async (e, allAsins = null) => {
        e.preventDefault();

        // Get ASINs from parameter or use state
        const asinsToAnalyze = allAsins || (asin ? [asin] : []);

        if (asinsToAnalyze.length === 0) {
            setError('Please enter a valid ASIN');
            return;
        }

        const effectiveSource = resolveSource();

        // If multiple ASINs, use bulk analyze API (shows popup for queued tasks)
        if (asinsToAnalyze.length > 1) {
            setLoading(true);
            setError('');
            setProductData(null);

            try {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const response = await fetch(`${API_URL}/listing_score/bulk-analyze`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${authCtx.token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ asins: asinsToAnalyze, source: effectiveSource })
                });

                // Handle 409 Conflict (duplicate request)
                if (response.status === 409) {
                    const errorData = await response.json();
                    setError(errorData.message || 'A similar request is already being processed. Please wait a moment and try again.');
                    setLoading(false);
                    return;
                }

                // Handle async task response (202 status) - show popup for bulk analyze
                if (response.status === 202) {
                    const data = await response.json();
                    if (data.task_id) {
                        // Update credits in real-time if provided in response
                        if (data.credits && typeof data.credits.remaining === 'number') {
                            authCtx.updateUserCredit(data.credits.remaining);
                        }
                        // Show queue notification popup (only for bulk/multiple ASINs)
                        setShowQueuePopup(true);
                        setLoading(false);
                        return;
                    }
                }

                // Handle error responses
                if (!response.ok) {
                    const errorData = await response.json();
                    setError(errorData.error || errorData.message || 'Failed to analyze listings');
                    setLoading(false);
                    return;
                }

                // If results are returned immediately (unlikely for bulk)
                const data = await response.json();
                if (data.success && data.results) {
                    // Redirect to bulk listing score page
                    navigate('/dashboard/bulk-listing-score', {
                        state: {
                            results: data.results,
                            asins: asinsToAnalyze
                        }
                    });
                }

            } catch (err) {
                setError('Network error. Please check your connection and try again.');
                setLoading(false);
            }
            return;
        }

        // Single ASIN - use existing single product API (instant results, no popup)
        const singleAsin = asinsToAnalyze[0];
        
        if (!singleAsin.trim()) {
            setError('Please enter a valid ASIN');
            return;
        }

        // Check credits only for rainforest source (open search)
        if (effectiveSource !== 'catalog') {
            try {
                const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
                const creditCheckResponse = await fetch(`${API_URL}/auth/get_user_credit`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${authCtx.token}`,
                        'Content-Type': 'application/json'
                    }
                });

                const creditData = await creditCheckResponse.json();

                if (!creditCheckResponse.ok) {
                    setError('Failed to verify credits. Please try again.');
                    return;
                }

                if (creditData.user_credit <= 0) {
                    setError('Insufficient credits. Please purchase more credits to continue.');
                    return;
                }
            } catch (err) {
                setError('Failed to verify credits. Please try again.');
                return;
            }
        }

        setLoading(true);
        setError('');
        setProductData(null);

        try {
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/listing_score/product?asin=${singleAsin}&source=${effectiveSource}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            // Check if ASIN not found error
            const errorMessage = data.error || data.message || '';
            if (response.status === 404 || 
                errorMessage.toLowerCase().includes('does not exist') ||
                errorMessage.toLowerCase().includes('not found')) {
                setShowAsinNotFoundModal(true);
                setError('');
                setLoading(false);
                return;
            }

            setProductData(data.data);
            
            // Update user credits if authenticated and credits are returned
            if (authCtx.isLoggedIn && data.credits && typeof data.credits.remaining === 'number') {
                authCtx.updateUserCredit(data.credits.remaining);
            }
            
            if (data.data && !data.data.analysis) {
                setError('Product data fetched successfully, but AI analysis is temporarily unavailable.');
            }

        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    // Reset handler for starting new analysis
    const handleReset = () => {
        setAsin('');
        setProductData(null);
        setError('');
        setSource('rainforest');
    };

    // Fetch products from SP-API catalogue
    const fetchProducts = async () => {
        try {
            setLoadingProducts(true);
            const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
            const response = await fetch(`${API_URL}/amazon/catalogue`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${authCtx.token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const data = await response.json();
                setProducts(data.products || []);
                setShowProductModal(true);
            } else {
                setError('Failed to fetch products from catalogue');
            }
        } catch (err) {
            setError('Network error. Please check your connection and try again.');
        } finally {
            setLoadingProducts(false);
        }
    };

    // Handle opening product catalogue modal
    const handleOpenProductModal = () => {
        fetchProducts();
    };

    // Handle closing product catalogue modal
    const handleCloseProductModal = () => {
        setShowProductModal(false);
        setSelectedAsins([]);
        setSelectAll(false);
    };

    // Handle ASIN selection from modal (for multiple selection)
    const handleAsinSelect = (asinValue) => {
        setSelectedAsins(prev => {
            if (prev.includes(asinValue)) {
                // Remove ASIN if already selected
                return prev.filter(asin => asin !== asinValue);
            } else {
                // Add ASIN if not selected
                return [...prev, asinValue];
            }
        });
    };

    // Handle select all
    const handleSelectAll = () => {
        const newSelectAll = !selectAll;
        setSelectAll(newSelectAll);
        if (newSelectAll) {
            // Select all
            setSelectedAsins(products.map(p => p.asin));
        } else {
            // Deselect all
            setSelectedAsins([]);
        }
    };

    // Sync selectAll state with selectedAsins
    useEffect(() => {
        if (products.length > 0) {
            setSelectAll(selectedAsins.length === products.length && products.length > 0);
        }
    }, [selectedAsins, products]);

    // State to pass multiple ASINs to SearchSection
    const [asinsToAdd, setAsinsToAdd] = useState([]);

    // Handle analyze from modal - add selected ASINs to the input tags
    const handleModalAnalyze = () => {
        if (selectedAsins.length > 0) {
            setShowProductModal(false);
            setSource('catalog'); // From own catalogue = free, no credits
            setAsinsToAdd(selectedAsins);
            setAsin(selectedAsins[0]);
            setSelectedAsins([]);
            setSelectAll(false);
        }
    };

    const handleUpgradeListing = (data) => {
        const pd = data || productData;
        if (!pd) return;
        navigateToSingleListingUpgrade(navigate, pd);
    };

    return(
        <div className={styles.container}>
            <header className={`${productData ? styles.header : `${styles.header}`} ${productData ? styles.headerWithResults : ''}`}>
                {productData ? (
                    <div className={styles.resultsHeader}>
                        <button
                            className={styles.backButtonResults}
                            onClick={handleReset}
                            aria-label="Back to Listing Score"
                        >
                           <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M20 8.75H4.7875L11.775 1.7625L10 0L0 10L10 20L11.7625 18.2375L4.7875 11.25H20V8.75Z" fill="#737373"/>
                            </svg>
                        </button>
                        <div className={styles.resultsHeaderContent}>
                            <h1 className={styles.resultsHeaderTitle}>Listing Score Analysis</h1>
                            <p className={styles.resultsHeaderSubtitle}>Real-time evaluation based on Amazon best practices</p>
                        </div>
                    </div>
                ) : null}
            </header>
            {!productData && (
                <>
                    <SearchSection
                        asin={asin}
                        setAsin={setAsin}
                        loading={loading}
                        error={error}
                        handleSubmit={handleSubmit}
                        onFetchFromCatalogue={handleOpenProductModal}
                        loadingProducts={loadingProducts}
                        asinsToAdd={asinsToAdd}
                        onAsinsAdded={() => setAsinsToAdd([])}
                    />
                </>
            )}

            {productData && productData.analysis && (
                <div id="listing-score-content">
                    <ResultsSection productData={productData} onUpgradeListing={handleUpgradeListing} />
                    <section className={styles.section2}>
                        <ActionPlanSection 
                            productData={productData} 
                            showDetailedAnalysis={showDetailedAnalysis}
                        />
                        <ComponentAnalysisSection productData={productData} />
                    </section>
                </div>
            )}

            
            {loading && <LoadingOverlay />}
            
            <AsinNotFoundModal 
                isOpen={showAsinNotFoundModal}
                onClose={() => setShowAsinNotFoundModal(false)}
            />

            {/* Queue Notification Popup */}
            <QueueNotificationPopup
                show={showQueuePopup}
                onClose={() => setShowQueuePopup(false)}
                onResetFiles={() => {
                    setShowQueuePopup(false);
                }}
                title="Listing Score Started"
            />

            {/* Product Selection Modal */}
            <SelectProductCatalogueModal
                show={showProductModal}
                onClose={handleCloseProductModal}
                products={products}
                selectedAsins={selectedAsins}
                onAsinSelect={handleAsinSelect}
                onSelectAll={handleSelectAll}
                onAnalyze={handleModalAnalyze}
                selectAll={selectAll}
            />
        </div>
    );
};

export default ListingScore;